/* One shared clock drives scene content and progress; chapter changes share a crossfade. */
(() => {
  'use strict';
  if (customElements.get('ws-how-it-works')) return;

  const clamp = (value) => Math.min(1, Math.max(0, value));
  const ease = (value, start, end) => {
    const amount = clamp((value - start) / (end - start));
    return amount * amount * (3 - 2 * amount);
  };
  const cursorEase = (value, start, end) => {
    const amount = clamp((value - start) / (end - start));
    return amount * amount * amount * (amount * (amount * 6 - 15) + 10);
  };
  // Rendered tip for a 20 x 28 SVG viewBox fitted into a 22 x 30 viewport.
  const cursorTip = { x: 2.4286, y: 1.6071 };
  const transitionOptions = { duration: 440, easing: 'cubic-bezier(.22,.68,0,1)' };

  class WeightStartHowItWorks extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._steps = [...this.querySelectorAll('[data-ws-how-step]')];
      this._buttons = [...this.querySelectorAll('[data-ws-how-button]')];
      this._bodies = [...this.querySelectorAll('[data-ws-how-body]')];
      this._scenes = [...this.querySelectorAll('[data-ws-how-scene]')];
      this._screens = [...this.querySelectorAll('.ws-how__screen')];
      this._stage = this.querySelector('.ws-how__stage');
      this._playback = this.querySelector('[data-ws-how-playback]');
      this._count = this.querySelector('[data-ws-how-count]');
      this._status = this.querySelector('[data-ws-how-status]');
      this._chapterControls = this.querySelector('.ws-how__chapter-controls');
      this._chapterControls.hidden = false;
      this._content = this.querySelector('.ws-how__content');
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._duration = Math.max(7000, Number(this.dataset.duration) || 9000);
      this._active = 0;
      this._elapsed = 0;
      this._lastTime = null;
      this._frame = null;
      this._layoutFrame = null;
      this._transitions = [];
      this._cursorTarget = { x: 59, y: -19 };
      this._inView = false;
      this._userPaused = false;
      this._manualHold = false;
      this._focusPaused = (this._content.contains(document.activeElement) || this._chapterControls.contains(document.activeElement));
      const signal = this._abort.signal;

      this._buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this._select(index, true);
          this._syncPlayback();
        }, { signal });
        button.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this._buttons.length;
          if (event.key === 'ArrowUp') next = (index - 1 + this._buttons.length) % this._buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this._buttons.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this._buttons[next].focus();
          this._select(next, true);
          this._syncPlayback();
        }, { signal });
      });

      [['[data-ws-how-previous]', -1], ['[data-ws-how-next]', 1]].forEach(([selector, direction]) => {
        this.querySelector(selector).addEventListener('click', () => {
          this._select((this._active + direction + this._steps.length) % this._steps.length, true);
          this._syncPlayback();
        }, { signal });
      });
      this._playback.addEventListener('click', () => {
        if (this._userPaused || this._manualHold) {
          this._userPaused = false;
          this._manualHold = false;
        } else {
          this._userPaused = true;
        }
        this._syncPlayback();
      }, { signal });

      // A focused step finishes its illustration, then holds without advancing.
      // The playback control is excluded so Play can resume with focus retained.
      this.addEventListener('focusin', () => {
        this._focusPaused = (this._content.contains(document.activeElement) || this._chapterControls.contains(document.activeElement));
        this._syncPlayback();
      }, { signal });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this._connected) return;
          this._focusPaused = (this._content.contains(document.activeElement) || this._chapterControls.contains(document.activeElement));
          this._syncPlayback();
        });
      }, { signal });
      document.addEventListener('visibilitychange', () => this._syncPlayback(), { signal });
      this._motion.addEventListener('change', () => {
        this._settleTransition();
        this._elapsed = 0;
        this._render();
        this._syncPlayback();
      }, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this || event.target.contains(this)) this._cleanup();
      }, { signal });

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          this._inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .12;
          this._syncPlayback();
        }, { threshold: [0, .12] });
        this._observer.observe(this._stage);
      } else {
        this._inView = true;
      }

      this._tick = (timestamp) => {
        this._frame = null;
        if (!this._canRun()) { this._lastTime = null; return; }
        if (this._lastTime !== null) this._elapsed += Math.max(0, timestamp - this._lastTime);
        this._lastTime = timestamp;
        if (this._elapsed >= this._duration) {
          if (this._focusPaused || this._manualHold) {
            this._elapsed = this._duration;
          } else {
            // A suspended tab never races through missed scenes when it returns.
            this._select((this._active + 1) % this._steps.length);
            this._lastTime = timestamp;
          }
        }
        this._render();
        if (this._canRun()) {
          this._frame = requestAnimationFrame(this._tick);
        } else {
          this._lastTime = null;
          this.dataset.playback = 'paused';
        }
      };
      this._select(0);
      this._measureLayout();
      const scheduleLayout = () => {
        if (this._layoutFrame !== null) return;
        this._layoutFrame = requestAnimationFrame(() => {
          this._layoutFrame = null;
          if (this._connected) this._measureLayout();
        });
      };
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => {
          // Height changes during the accordion transition need no remeasurement.
          const width = `${this._stage.clientWidth}:${this._content.clientWidth}`;
          if (width === this._layoutWidth) return;
          this._layoutWidth = width;
          scheduleLayout();
        });
        this._resizeObserver.observe(this._stage);
        this._resizeObserver.observe(this._content);
      } else {
        window.addEventListener('resize', scheduleLayout, { signal });
      }
      document.fonts?.addEventListener('loadingdone', scheduleLayout, { signal });
      document.fonts?.ready.then(() => {
        if (this._connected && !signal.aborted) scheduleLayout();
      });
      this._syncPlayback();
    }

    _canRun() {
      return this._connected && this._inView && !document.hidden &&
        !this._userPaused && !this._motion.matches &&
        (!(this._focusPaused || this._manualHold) || this._elapsed < this._duration);
    }

    _select(index, manual = false) {
      if (manual) {
        this._manualHold = true;
        this._status.textContent = `Step ${index + 1} of ${this._steps.length}. ${this._buttons[index].textContent.trim().replace(/^\d+\s*/, '')}`;
      }
      const chapterChanged = this._active !== index;
      if (!chapterChanged && this._hasSelected && !(manual && this._userPaused)) return;
      this._hasSelected = true;
      const animate = chapterChanged && (manual || this._inView) && this._canTransition() &&
        typeof this._scenes[index].animate === 'function';
      // Read the in-flight visual state before cancelling. Rapid clicks can
      // reverse any fade or height change without flashing to its endpoint.
      const scenesFrom = animate ? this._scenes.map((scene) =>
        scene.hidden ? 0 : Number(getComputedStyle(scene).opacity)) : [];
      const bodiesFrom = animate ? this._bodies.map((body) => ({
        height: body.hidden ? 0 : body.getBoundingClientRect().height,
        opacity: body.hidden ? 0 : Number(getComputedStyle(body).opacity)
      })) : [];
      this._cancelTransition();
      this._active = index;
      this._elapsed = manual && this._userPaused ? this._duration : 0;
      this._lastTime = null;
      this._steps.forEach((step, position) => {
        const active = position === index;
        step.classList.toggle('ws-how__step--active', active);
        step.style.removeProperty('--ws-how-progress');
        this._buttons[position].setAttribute('aria-expanded', String(active));
        this._bodies[position].setAttribute('aria-hidden', String(!active));
        this._bodies[position].inert = !active;
      });
      // These decorative scenes intentionally have no live region. Automatic
      // advancement neither announces content nor moves keyboard focus.
      this._count.textContent = `${index + 1} / ${this._steps.length}`;
      this.dataset.activeStep = String(index + 1);
      this._render();
      if (!animate) { this._settleTransition(); return; }
      const startTime = document.timeline.currentTime;
      const run = (element, keyframes, options = transitionOptions) => {
        const animation = element.animate(keyframes, options);
        if (startTime !== null) animation.startTime = startTime;
        this._transitions.push(animation);
        animation.onfinish = () => {
          this._transitions = this._transitions.filter((item) => item !== animation);
          if (!this._transitions.length) this._settleTransition();
        };
      };
      this._scenes.forEach((scene, position) => {
        const opacity = position === index ? 1 : 0;
        scene.hidden = !opacity && scenesFrom[position] === 0;
        scene.style.opacity = String(opacity);
        if (!scene.hidden) {
          const from = scenesFrom[position];
          // Fade the old text early; the next card follows with a short overlap.
          const keyframes = opacity ? [
            { opacity: from, offset: 0 },
            { opacity: from, offset: from > 0 ? 0 : .18, easing: 'ease-out' },
            { opacity: 1, offset: 1 }
          ] : [
            { opacity: from, offset: 0, easing: 'ease-out' },
            { opacity: 0, offset: .44 },
            { opacity: 0, offset: 1 }
          ];
          run(scene, keyframes, { duration: transitionOptions.duration, easing: 'linear' });
        }
      });
      this._bodies.forEach((body, position) => {
        const active = position === index;
        const from = bodiesFrom[position];
        const height = active ? this._bodyHeight : 0;
        body.hidden = !active && from.height === 0;
        body.style.minHeight = '0px';
        body.style.height = `${height}px`;
        body.style.opacity = active ? '1' : '0';
        if (!body.hidden) run(body, [
          { height: `${from.height}px`, opacity: from.opacity },
          { height: `${height}px`, opacity: active ? 1 : 0 }
        ]);
      });
    }

    _canTransition() {
      // A manual selection below the stage still animates its description.
      return this._connected && !document.hidden &&
        !this._userPaused && !this._motion.matches;
    }

    _cancelTransition() {
      this._transitions.forEach((animation) => {
        animation.onfinish = null;
        animation.cancel();
      });
      this._transitions = [];
    }

    _settleTransition() {
      this._cancelTransition();
      this._scenes.forEach((scene, index) => {
        scene.hidden = index !== this._active;
        scene.style.removeProperty('opacity');
      });
      this._bodies.forEach((body, index) => {
        body.hidden = index !== this._active;
        body.style.removeProperty('min-height');
        body.style.removeProperty('height');
        body.style.removeProperty('opacity');
      });
    }

    _measureLayout() {
      this._settleTransition();
      this.style.removeProperty('--ws-how-card-height');
      this.style.removeProperty('--ws-how-body-height');
      // All panels are measured in one task, then hidden before the next paint.
      // A shared size keeps the card edge, CTA and section below from jumping.
      this._scenes.forEach((scene) => { scene.hidden = false; });
      this._bodies.forEach((body) => { body.hidden = false; });
      const cardHeight = Math.ceil(Math.max(...this._screens.map((screen) => screen.getBoundingClientRect().height)));
      const minimum = parseFloat(getComputedStyle(this).getPropertyValue('--ws-how-body-min-height')) || 124;
      this._bodyHeight = Math.ceil(Math.max(minimum, ...this._bodies.map((body) => body.scrollHeight)));
      this.style.setProperty('--ws-how-card-height', `${cardHeight}px`);
      this.style.setProperty('--ws-how-body-height', `${this._bodyHeight}px`);
      const choice = this._scenes[0].querySelector('.ws-how__choice--selected');
      if (choice) {
        const sceneBounds = this._scenes[0].getBoundingClientRect();
        const choiceBounds = choice.getBoundingClientRect();
        this._cursorTarget = {
          x: choiceBounds.left + choiceBounds.width * .78 - sceneBounds.left - sceneBounds.width / 2,
          y: choiceBounds.top + choiceBounds.height / 2 - sceneBounds.top - sceneBounds.height / 2
        };
      }
      this._settleTransition();
      this._layoutWidth = `${this._stage.clientWidth}:${this._content.clientWidth}`;
      this._render();
    }

    _render() {
      const reduced = this._motion.matches;
      const progress = reduced ? 1 : clamp(this._elapsed / this._duration);
      const sceneTime = reduced ? .9 : progress;
      const scene = this._scenes[this._active];
      this._steps[this._active].style.setProperty('--ws-how-progress', progress.toFixed(4));
      const set = (key, value) => scene.style.setProperty(`--ws-how-${key}`, value.toFixed(4));

      if (this._active === 0) {
        const travel = cursorEase(sceneTime, .12, .33);
        const curve = 4 * travel * (1 - travel);
        const press = cursorEase(sceneTime, .34, .365) * (1 - cursorEase(sceneTime, .365, .405));
        set('select', ease(sceneTime, .35, .40));
        set('second', ease(sceneTime, .46, .56));
        set('reveal', ease(sceneTime, .62, .72));
        set('cursor-opacity', reduced ? 0 : cursorEase(sceneTime, .08, .13) * (1 - cursorEase(sceneTime, .61, .69)));
        // The curved approach shares the clock; its speed and acceleration settle at both ends.
        set('cursor-x', this._cursorTarget.x - cursorTip.x + (1 - travel) * 61 + curve * 10);
        set('cursor-y', this._cursorTarget.y - cursorTip.y + (1 - travel) * 99 - curve * 6);
        set('cursor-scale', 1 - press * .06);
      } else if (this._active === 1) {
        set('select', ease(sceneTime, .17, .27));
        set('second', ease(sceneTime, .43, .53));
        set('reveal', ease(sceneTime, .65, .75));
      } else if (this._active === 2) {
        set('select', ease(sceneTime, .24, .34));
        set('second', ease(sceneTime, .47, .57));
        set('reveal', ease(sceneTime, .61, .71));
      } else {
        set('select', ease(sceneTime, .19, .29));
        set('second', ease(sceneTime, .43, .58));
        set('reveal', ease(sceneTime, .7, .8));
      }
    }

    _syncPlayback() {
      if (!this._connected) return;
      if (!this._canTransition()) this._settleTransition();
      this._playback.hidden = this._motion.matches;
      const paused = this._userPaused || this._manualHold;
      this._playback.setAttribute('aria-label', paused ? 'Play automatic steps' : 'Pause automatic steps');
      this._playback.querySelector('[data-ws-how-playback-text]').textContent = this._manualHold && !this._userPaused ? 'Auto-play' : paused ? 'Play' : 'Pause';
      this._playback.querySelector('.ws-how__pause-icon').toggleAttribute('hidden', paused);
      this._playback.querySelector('.ws-how__play-icon').toggleAttribute('hidden', !paused);
      if (this._canRun()) {
        if (this._frame === null && this._tick) this._frame = requestAnimationFrame(this._tick);
      } else {
        if (this._frame !== null) cancelAnimationFrame(this._frame);
        this._frame = null;
        this._lastTime = null;
      }
      this.dataset.playback = this._canRun() ? 'playing' : 'paused';
    }

    _cleanup() {
      if (!this._connected) return;
      this._connected = false;
      if (this._frame !== null) cancelAnimationFrame(this._frame);
      this._frame = null;
      this._lastTime = null;
      if (this._layoutFrame !== null) cancelAnimationFrame(this._layoutFrame);
      this._layoutFrame = null;
      this._hasSelected = false;
      this._settleTransition();
      this._observer?.disconnect();
      this._resizeObserver?.disconnect();
      this._abort.abort();
    }

    disconnectedCallback() { this._cleanup(); }
  }

  // Custom element callbacks also handle Shopify section re-render/load events.
  customElements.define('ws-how-it-works', WeightStartHowItWorks);
})();
